import { type ReactElement, useCallback, useMemo } from 'react'
import { useQueryState } from 'nuqs'
import { parseAsRatioId } from '@util'
import { isDefined, isFormulaRatioData, isFractionalRatioData, type RatioId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import {
  getVariantDisplayLabel,
  useProfile,
  useRatios,
  useReportColumns,
  useSafeCurrentWorkflow,
  useTranslation,
} from '@front/model'
import { NotReady, Page, TDrawer, WorkflowSelect } from '@components'
import { EditFractionalStatForm } from './fractional/EditFractionalStatForm'
import { EditFormulaStatForm } from './formula/EditFormulaStatForm'
import { CustomStatsTable } from './CustomStatsTable'
import { AddRatioDrawer } from './AddRatioDrawer'

function CustomRatiosSetting(): ReactElement {
  const { t } = useTranslation()
  const { user, hasPermission, isLoading } = useProfile()

  const workflowId = useSafeCurrentWorkflow()?.workflow.id
  const {
    data: ratios,
    isLoading: isLoadingRatios,
    isFetching,
    refetch,
  } = useRatios({ scopeId: user?.companyId, workflowId })

  const [editRatioId, setEditRatioId] = useQueryState<RatioId>('edit', parseAsRatioId)

  const openEdit = useCallback((id: RatioId) => setEditRatioId(id), [setEditRatioId])
  const closeEdit = useCallback(() => setEditRatioId(null), [setEditRatioId])

  const selectedEditRatio = ratios?.find((r) => r.ratioId === editRatioId)

  const { data: allReportColumns = [] } = useReportColumns(workflowId)
  const reportColumnLabelMap = useMemo(
    () => new Map(allReportColumns.map((column) => [column.value, getVariantDisplayLabel(column, allReportColumns)])),
    [allReportColumns]
  )
  const validMetricValues = useMemo(
    () => new Set(allReportColumns.filter((column) => column.type === 'number').map((column) => column.value)),
    [allReportColumns]
  )

  if (isLoading) return <NotReady isLoading />
  if (!hasPermission('ratio:save')) return <NotReady error={t('You do not have permission to manage custom stats')} />

  return (
    <>
      <Page>
        <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Custom Stats')}>
          <WorkflowSelect />
          <AddRatioDrawer entityId={user?.companyId} />
        </Page.Heading>
        <Page.Content>
          <CustomStatsTable
            ratios={ratios ?? []}
            isLoading={isLoadingRatios}
            reportColumnLabelMap={reportColumnLabelMap}
            validMetricValues={validMetricValues}
            onRowClick={openEdit}
          />
        </Page.Content>
      </Page>
      <TDrawer opened={isDefined(selectedEditRatio)} size='lg' title={t('Edit Ratio')} onClose={closeEdit}>
        {isDefined(selectedEditRatio) && isFormulaRatioData(selectedEditRatio) && (
          <EditFormulaStatForm formula={selectedEditRatio} onClose={closeEdit} />
        )}
        {isDefined(selectedEditRatio) && isFractionalRatioData(selectedEditRatio) && (
          <EditFractionalStatForm ratio={selectedEditRatio} onClose={closeEdit} />
        )}
      </TDrawer>
    </>
  )
}

export const Route = createFileRoute('/settings/ratios/')({
  component: CustomRatiosSetting,
})
